import Reveal from "../components/Reveal";

const LAST_UPDATED = "23 April 2026";
const COMPANY = "Silly Butterfly";
const EMAIL = "hello@sillybutterfly.com";

const H2 = {
  fontFamily: "var(--font-display)",
  fontSize: "1.2rem",
  fontWeight: 600,
  color: "var(--plum)",
  margin: "0 0 12px",
};

const P = {
  color: "var(--text-secondary)",
  lineHeight: 1.8,
  fontSize: "0.95rem",
  margin: "0 0 14px",
};

const LI = {
  color: "var(--text-secondary)",
  lineHeight: 1.8,
  fontSize: "0.95rem",
  marginBottom: "6px",
  paddingLeft: "4px",
};

const PROSE_SECTION = {
  paddingBottom: "32px",
  marginBottom: "32px",
  borderBottom: "1px solid rgba(212,168,83,0.15)",
};

const PROSE_SECTION_LAST = {
  paddingBottom: 0,
  marginBottom: 0,
};

export default function Terms() {
  return (
    <main style={{ paddingTop: "var(--nav-h)" }}>
      {/* Content */}
      <section className="sb-section">
        <div className="sb-container" style={{ maxWidth: "980px" }}>
          <Reveal>
            <div style={{
              background: "var(--white)", borderRadius: "var(--radius-lg)",
              padding: "clamp(32px, 5vw, 56px)",
              boxShadow: "var(--shadow-md)",
              border: "1px solid rgba(212,168,83,0.08)",
            }}>

              <div style={PROSE_SECTION}>
                <span style={{ display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "0.78rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--gold)", marginBottom: "10px" }}>
                  Legal
                </span>
                <h1 style={{ color: "var(--plum)", margin: "0 0 10px" }}>
                  Terms &amp; Conditions
                </h1>
                <p style={{ ...P, marginBottom: 0, fontSize: "0.85rem" }}>
                  Last updated: {LAST_UPDATED}
                </p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>1. About These Terms</h2>
                <p style={P}>These Terms and Conditions govern your use of our website and the booking of any services provided by {COMPANY} ("we", "us", "our"). By making a booking or enquiry, you agree to be bound by these terms.</p>
                <p style={P}>We reserve the right to update these terms at any time. The most current version will always be available on this page.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>2. Our Services</h2>
                <p style={P}>{COMPANY} provides mobile cocktail making classes and cocktail service experiences for private events, hen parties, birthdays, corporate events, and team socials across the United Kingdom.</p>
                <p style={P}>All services are subject to availability and are confirmed only upon receipt of a deposit and written confirmation from us.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>3. Bookings & Deposits</h2>
                <p style={P}>A booking is not confirmed until a non-refundable deposit has been received and a written confirmation has been issued by {COMPANY}.</p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}>A deposit of 25% of the total booking value is required to secure your date.</li>
                  <li style={LI}>The remaining balance is due no later than 14 days before your event date.</li>
                  <li style={LI}>Late payment may result in cancellation of your booking.</li>
                  <li style={LI}>Bookings made within 14 days of the event date require full payment at the time of booking.</li>
                </ul>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>4. Payment</h2>
                <p style={P}>All prices are quoted in British Pounds (GBP) and include VAT where applicable. Payment can be made via bank transfer or any other method agreed upon in writing.</p>
                <p style={P}>Prices quoted are valid for 30 days from the date of the quote. We reserve the right to amend pricing after this period.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>5. Cancellations & Refunds</h2>
                <p style={P}>We understand that circumstances can change. Our cancellation policy is as follows:</p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}><strong>More than 30 days before the event:</strong> Full refund of any balance paid, excluding the deposit.</li>
                  <li style={LI}><strong>14–30 days before the event:</strong> 50% of the total booking value will be charged.</li>
                  <li style={LI}><strong>Less than 14 days before the event:</strong> The full booking value is non-refundable.</li>
                  <li style={LI}>All cancellations must be made in writing via email to {EMAIL}.</li>
                </ul>
                <p style={P}>We reserve the right to cancel a booking in exceptional circumstances (e.g. severe weather, illness, or events beyond our control). In such cases, we will offer a full refund or an alternative date.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>6. Your Responsibilities</h2>
                <p style={P}>As the event organiser, you are responsible for:</p>
                <ul style={{ paddingLeft: "20px", margin: "0 0 14px" }}>
                  <li style={LI}>Ensuring the venue is suitable and accessible for our team and equipment.</li>
                  <li style={LI}>Providing accurate guest numbers at least 7 days before the event.</li>
                  <li style={LI}>Ensuring all guests are of legal drinking age (18+) or clearly identified as non-drinkers.</li>
                  <li style={LI}>Ensuring the event environment is safe for our staff.</li>
                  <li style={LI}>Notifying us of any dietary requirements, allergies, or accessibility needs in advance.</li>
                </ul>
                <p style={P}>We reserve the right to refuse service to any guest who appears intoxicated or behaves in a manner that is unsafe or abusive towards our staff.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>7. Our Liability</h2>
                <p style={P}>{COMPANY} will not be liable for any loss, damage, or injury arising from events beyond our reasonable control, including but not limited to: acts of God, severe weather, transport disruptions, or venue-related issues not caused by us.</p>
                <p style={P}>Our total liability to you shall not exceed the total amount paid for the services booked.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>8. Intellectual Property</h2>
                <p style={P}>All content on this website — including text, images, logos, and brand assets — is the property of {COMPANY} and may not be reproduced, distributed, or used without our prior written consent.</p>
              </div>

              <div style={PROSE_SECTION}>
                <h2 style={H2}>9. Governing Law</h2>
                <p style={P}>These Terms and Conditions are governed by the laws of England and Wales. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
              </div>

              <div style={PROSE_SECTION_LAST}>
                <h2 style={H2}>10. Contact Us</h2>
                <p style={{ ...P, marginBottom: "6px" }}>If you have any questions about these Terms and Conditions, please contact us:</p>
                <p style={P}>
                  <strong style={{ color: "var(--plum)" }}>{COMPANY}</strong><br />
                  <a href={`mailto:${EMAIL}`} style={{ color: "var(--gold)" }}>{EMAIL}</a>
                </p>
              </div>

            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
